import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import FontAwesomeIcons from 'react-native-vector-icons/FontAwesome';
import Ionicons from 'react-native-vector-icons/Ionicons';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { HStack, Box, VStack } from 'native-base';
import { isEmpty, reduce } from 'lodash';

import { Text } from '@pimm/base';
import { AlarmLevel, EventPriority } from '@pimm/services/lib/store-equipment';
import { tempValue } from '@app/utils/string-formatter';
import { ResourceLoader } from '@app/components/shared';
import { getEventPriorityColor, useEquipmentTranslate, useGetInstances } from '@app/features/store-equipment';
import { useSiteConfig } from '@app/features/store-core';
import { WidgetBox, WidgetBoxProps } from '../widget-box';
import { useAppLocale } from '@pimm/common';

interface TempBoxProps {
  title?: string;
  temp: number;
  isOpenDoor: boolean;
  color?: string;
  isOffline: boolean;
}

interface EventCounts {
  [key: string]: {
    count: number;
    eventPriority: EventPriority | undefined;
  };
}


export const WidgetTemperatures = ({ ...props }: WidgetBoxProps) => {
  const { t } = useTranslation();
  const { translate } = useAppLocale();
  const { et } = useEquipmentTranslate();
  const { siteConfig } = useSiteConfig();
  const instances = useGetInstances({ siteId: siteConfig?.id ?? '' });

  // const chunks: EquipmentBoxProps[][] = useMemo(() => {
  //   // Get all instance with event priority other than "None"

  //   return chunk(equipment, 2);
  // }, [instances]);

  const { equipment, temperatures } = useMemo(() => {
    if (isEmpty(instances)) return {};

    let temperatures = [] as TempBoxProps[]; // Type assertion

    const flatMapInstances = ['walk'].map(key => {
      if (!Array.isArray(instances.data)) return [];
      return instances.data
        .filter(instance => !!instance.Instances?.Temperature?.ThresholdProfile?.DisplayGroup?.toLowerCase().includes(key))
        .map(instance => instance.Instances);
    })[0];

    const events: EventCounts = reduce(instances.data, (instanceData: EventCounts, device) => {
      Object.keys(device.Instances ?? {}).forEach(name => {
        const instance = device.Instances?.[name];
        // Check for valid event priority
        if (instance?.EventPriority && instance.EventPriority !== 'None') {
          // Initialize or update the count and eventPriority
          if (!instanceData[name]) {
            instanceData[name] = { count: 1, eventPriority: instance.EventPriority };
          } else {
            instanceData[name].count += 1;
            // Update the eventPriority if needed
            instanceData[name].eventPriority = instance.EventPriority;
          }
        }
      });
      return instanceData;
    }, {}); // prettier-ignore

    if (!isEmpty(flatMapInstances)) {
      const transformedArray = flatMapInstances.map(instance => {
        const Temperature = instance?.Temperature;

        if (!Temperature) return null;

        const temp: TempBoxProps = {
          isOffline: !Temperature.IsOnline,
          title: Temperature.Description,
          temp: parseFloat(Number(Temperature?.StaticValue || 0).toFixed(1)),
          isOpenDoor: false,
          color: 'gray',
        };

        if (Temperature?.IsOnline) {
          switch (Temperature?.AlarmLevel) {
            case AlarmLevel.Info:
              temp.color = 'moss';
              break;
            case AlarmLevel.Warning:
              temp.color = 'warning';
              break;
            case AlarmLevel.Alarm:
              temp.color = 'error';
              break;
            case AlarmLevel.Dead:
              temp.color = 'gray';
              break;
            default:
              temp.color = 'blue';
              break;
          }
        }
        return temp;
      });

      temperatures = transformedArray.filter(item => item !== null);
    }

    // List of Equipment Display
    const equipment = [
      {
        icon: <Ionicons name="thermometer-outline" size={14} color="gray.800" />,
        value: events?.Temperature?.count ?? 0,
        bgColor: getEventPriorityColor(events?.Temperature?.eventPriority) || 'gray.600',
      },
      {
        icon: <MaterialCommunityIcons name="door-open" size={14} color="gray.800" />,
        value: events?.Door?.count ?? 0,
        bgColor: getEventPriorityColor(events?.Door?.eventPriority) || 'gray.600',
      },
      {
        icon: <MaterialIcons name="wifi-off" size={14} color="gray.800" />,
        value: events?.SignalStrength?.count ?? 0,
        bgColor: getEventPriorityColor(events?.SignalStrength?.eventPriority) || 'gray.600',
      },
      {
        icon: <FontAwesomeIcons name="battery-quarter" size={14} color="gray.800" />,
        value: events?.BatteryVoltage?.count ?? 0,
        bgColor: getEventPriorityColor(events?.BatteryVoltage?.eventPriority) || 'gray.600',
      },
    ];

    return { equipment, temperatures };
  }, [instances?.data]);

  return (
    <WidgetBox hideTitle p={1} {...props}>
      <HStack alignItems="center" justifyContent="space-between" px={1} h={7}>
        <Box flex={1}>
          <Text
            size="sm"
            fontWeight={700}
            color="gray.600"
            lineHeight="md"
            textTransform="uppercase"
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {translate(props?.title, props?.titleTranslations)}
          </Text>
        </Box>

        <Box rounded="md" justifyContent="center" px={1.5} h={4} bg="success.50">
          <Text size="sm" color="success.600" fontWeight={500}>
            {t('widgets:online')}
          </Text>
        </Box>
      </HStack>

      <ResourceLoader
        minH="90px"
        flex={1}
        pt={1}
        px={1.5}
        pb={1.5}
        isEmpty={instances.isError || isEmpty(temperatures)}
        isLoading={instances.isLoading}
        _text={{ size: 'sm' }}
      >
        <VStack space={1.5} px={0.5}>
          <HStack alignItems="center">
            {equipment?.map((_equipment, index) => {
              return (
                <Box key={`widget.temperatures.equipment.${index}`} w="1/4" px={0.5}>
                  <HStack alignItems="center" justifyContent="space-between" pr={2} h={6} rounded="md" bgColor="gray.100">
                    <Box p={1} rounded="full">
                      {_equipment.icon}
                    </Box>
                    <Text size="md" color="black" fontWeight={700}>
                      {_equipment.value.toString()}
                    </Text>
                  </HStack>
                </Box>
              );
            })}
          </HStack>

          <HStack flexWrap="wrap">
            {temperatures?.map((_temp, index) => (
              <Box key={`widget.temperatures.${index}`} p={0.5} w="1/2">
                <HStack alignItems="center" justifyContent="space-between" p={2} rounded="md" bgColor={`${_temp.color}.200`}>
                  <Box flex={1}>
                    <Text size="md" color="black" fontWeight={600} lineHeight="2xs" maxW='3/4' minW='1/2' numberOfLines={2} ellipsizeMode="tail">
                      {et(_temp.title)}
                    </Text>
                  </Box>

                  {!_temp.isOffline ? (
                    <HStack space={1}>
                      {_temp.isOpenDoor && <MaterialCommunityIcons name="door-open" color={`${_temp.color}.900`} size={16} />}
                      <Text size="lg" color={`${_temp.color}.900`} fontWeight={600} lineHeight="xs" numberOfLines={2} ellipsizeMode="tail" >
                        {`${tempValue(_temp?.temp)}`}
                      </Text>
                    </HStack>
                  ) : (
                    <Box>
                      <Text size="md" fontWeight={700} color="gray.700" lineHeight="md">
                        {t('store_equipment:offline')}
                      </Text>
                    </Box>
                  )}
                </HStack>
              </Box>
            ))}
          </HStack>
        </VStack>
      </ResourceLoader>
    </WidgetBox>
  );
};
